import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { demeterPresentationTemplatesApi } from '../../../Apis/Apis';
import {
    DemeterPageElementModel,
    DemeterUserType,
    PresentationTemplateModel,
    PresentationTemplateSlideModel,
    UpsertPresentationTemplateRequest,
} from '../../../Generated/Raven-Demeter';
import { useApplicationSelector } from '../../../Redux/ReduxStore';
import { selectUserType } from '../../../Redux/Slices/UserSlice';
import useLanguage from '../../Services/Language/useLanguageHook';
import useApi from './useApiHook';
import useApiWithoutAutoExecute from './useApiWithoutAutoExecute';

interface UsePresentationTemplatesApiResult {
    presentationTemplates: PresentationTemplateModel[];
    isLoadingPresentationTemplates: boolean;
    isUpsertPresentationTemplateLoading: boolean;
    addPageElementToPresentationTemplate: (presentationTemplateGuid: string, pageElement: DemeterPageElementModel) => Promise<void>;
    refreshPresentationTemplates: () => void;
}

// Shared state across all hook instances to prevent stale data
// Example: adding a page element to a dashboard from the projection then seasonal chart on the same page
let sharedPresentationTemplates: PresentationTemplateModel[] = [];
const sharedStateListeners: Array<(presentationTemplates: PresentationTemplateModel[]) => void> = [];

const notifyAllListeners = (presentationTemplates: PresentationTemplateModel[]) => {
    sharedPresentationTemplates = presentationTemplates;
    sharedStateListeners.forEach((listener) => listener(presentationTemplates));
};

const usePresentationTemplatesApi = (): UsePresentationTemplatesApiResult => {
    const [presentationTemplates, setPresentationTemplates] = useState<PresentationTemplateModel[]>(sharedPresentationTemplates);
    const [translations] = useLanguage();
    const currentUserType = useApplicationSelector(selectUserType);
    const isRegularUser = useMemo(() => currentUserType === DemeterUserType.Regular, [currentUserType]);

    const [isLoadingPresentationTemplates, refreshPresentationTemplates, listPresentationTemplatesResponse] = useApi(() => {
        if (isRegularUser) {
            return null;
        }

        return demeterPresentationTemplatesApi.listPresentationTemplates(undefined, undefined, undefined, undefined);
    });

    const [isUpsertPresentationTemplateLoading, upsertPresentationTemplate] = useApiWithoutAutoExecute(
        (request: UpsertPresentationTemplateRequest | undefined) => {
            if (!request) {
                return null;
            }

            return demeterPresentationTemplatesApi.upsertPresentationTemplate(request);
        },
        {
            successMessage: translations.presentationTemplates.success.updatePresentationTemplateSuccess,
            errorMessage: translations.presentationTemplates.errors.updatePresentationTemplateError,
        },
    );

    const pendingPresentationTemplatesUpdatesReference = useRef(0);

    useEffect(() => {
        const listener = (updatedPresentationTemplates: PresentationTemplateModel[]) => {
            setPresentationTemplates(updatedPresentationTemplates);
        };
        sharedStateListeners.push(listener);

        return () => {
            const index = sharedStateListeners.indexOf(listener);
            if (index > -1) {
                sharedStateListeners.splice(index, 1);
            }
        };
    }, []);

    const addPageElementToPresentationTemplate = useCallback(
        async (presentationTemplateGuid: string, pageElement: DemeterPageElementModel): Promise<void> => {
            const targetPresentationTemplate = presentationTemplates.find(
                (presentationTemplate) => presentationTemplate.presentationTemplateGuid === presentationTemplateGuid,
            );

            if (!targetPresentationTemplate) {
                return;
            }

            const slides = targetPresentationTemplate.rows || [];
            slides.push({
                rows: [pageElement],
            } as PresentationTemplateSlideModel);

            const upsertRequest: UpsertPresentationTemplateRequest = {
                presentationTemplateGuid: targetPresentationTemplate.presentationTemplateGuid,
                name: targetPresentationTemplate.name,
                format: targetPresentationTemplate.format,
                rows: slides,
            };

            await upsertPresentationTemplate(upsertRequest);
            pendingPresentationTemplatesUpdatesReference.current += 1;
        },
        [presentationTemplates, upsertPresentationTemplate],
    );

    useEffect(() => {
        if (isUpsertPresentationTemplateLoading || !pendingPresentationTemplatesUpdatesReference.current) {
            return;
        }

        refreshPresentationTemplates();
    }, [isUpsertPresentationTemplateLoading]);

    useEffect(() => {
        if (isLoadingPresentationTemplates || !listPresentationTemplatesResponse || !listPresentationTemplatesResponse.rows) {
            return;
        }

        notifyAllListeners(listPresentationTemplatesResponse.rows);
        pendingPresentationTemplatesUpdatesReference.current = 0;
    }, [listPresentationTemplatesResponse, isLoadingPresentationTemplates]);

    return {
        presentationTemplates,
        isLoadingPresentationTemplates,
        isUpsertPresentationTemplateLoading,
        addPageElementToPresentationTemplate,
        refreshPresentationTemplates,
    };
};

export default usePresentationTemplatesApi;
